/**
 * Permissão efetiva (SPEC-v2 2.5): `ROLE_PERMISSIONS[role]` ∪ `ROLE_SUITE_PERMISSIONS[role]` ∪
 * permissões das funções do Estúdio. Calculada aqui, do lado do provider (na V2, o login de
 * demonstração roda na thread principal; na fase 2, no servidor), e entregue pronta na
 * `SessionInfo`. A interface nunca recalcula: lê `session.suitePermissions` e afins.
 */
import {
  type Area,
  DEFAULT_FUNCTIONS_BY_ROLE,
  type Id,
  type Ms,
  type Permission,
  ROLE_PERMISSIONS,
  ROLE_SUITE_PERMISSIONS,
  type Role,
  type SessionInfo,
  STUDIO_FUNCTION_PERMISSIONS,
  type StudioFunction,
  type StudioPermission,
  type SuitePermission,
} from '@rota/contracts';

export const AREAS: readonly Area[] = ['painel', 'automacoes', 'estudio', 'palco'];

/**
 * Palco (docs/palco/SPEC.md 4): quem vê a área e quem mexe nos vídeos. Os contratos da suíte ainda
 * não têm `area:palco`; a regra fica aqui, ao lado das outras, e entra pronta na `SessionInfo`
 * (`areas`). `manage` = enviar, renomear, apagar e ajustar o player; `view` = métricas e código
 * do embed. Suporte e TV não veem.
 */
export type PalcoAccess = 'manage' | 'view';
export const PALCO_ACCESS: Readonly<Record<Role, PalcoAccess | null>> = {
  owner: 'manage',
  partner: 'manage',
  creative: 'manage',
  traffic: 'view',
  tech: 'view',
  expert: 'view',
  support: null,
  tv: null,
};

export function palcoAccessFor(role: Role | null | undefined): PalcoAccess | null {
  return role ? PALCO_ACCESS[role] : null;
}

export function areasFor(perms: readonly SuitePermission[], role?: Role): Area[] {
  return AREAS.filter((a) =>
    a === 'palco' ? palcoAccessFor(role) != null : perms.includes(`area:${a}` as SuitePermission),
  );
}

export function studioPermissionsFor(role: Role, functions: readonly StudioFunction[]): StudioPermission[] {
  const out = new Set<StudioPermission>();
  for (const f of functions) for (const p of STUDIO_FUNCTION_PERMISSIONS[f]) out.add(p);
  // `ads:upload` exige também `ads:act` da V1 (2.5).
  if (!ROLE_PERMISSIONS[role].includes('ads:act')) out.delete('ads:upload');
  return [...out];
}

export function defaultFunctionsFor(role: Role): StudioFunction[] {
  return [...DEFAULT_FUNCTIONS_BY_ROLE[role]];
}

/** Tela inicial por papel e função (tabela 2.5). Quem tem 2+ áreas e é owner/partner abre o lançador. */
export function homeFor(role: Role, _functions: readonly StudioFunction[]): string {
  switch (role) {
    case 'owner':
    case 'partner':
      return '/';
    case 'traffic':
      return '/painel/decisoes';
    case 'creative':
      return '/estudio/quadro?dono=eu';
    case 'support':
      return '/automacoes/conversas';
    case 'expert':
      return '/estudio';
    case 'tech':
      return '/painel/rastreamento';
    case 'tv':
      return '/tv';
  }
}

export interface PersonForSession {
  id: Id;
  name: string;
  initials: string;
  email: string;
  role: Role;
  functions: StudioFunction[];
  mfaEnrolled: boolean;
}

export function buildSession(
  p: PersonForSession,
  s: { expiresAt: Ms; stepUpUntil: Ms | null; remembered: boolean },
): SessionInfo {
  const permissions: readonly Permission[] = ROLE_PERMISSIONS[p.role];
  const suitePermissions = ROLE_SUITE_PERMISSIONS[p.role];
  return {
    viewer: { id: p.id, name: p.name, initials: p.initials, role: p.role, permissions },
    email: p.email,
    functions: [...p.functions],
    suitePermissions,
    studioPermissions: studioPermissionsFor(p.role, p.functions),
    areas: areasFor(suitePermissions, p.role),
    home: homeFor(p.role, p.functions),
    // V2: 2FA opcional (mock). Na fase 2 é obrigatório para owner e para papéis sensíveis (10.2).
    mfa: { enrolled: p.mfaEnrolled, required: false },
    expiresAt: s.expiresAt,
    stepUpUntil: s.stepUpUntil,
    remembered: s.remembered,
  };
}
